'use client';

import { useEffect, useRef, useState } from 'react';
import { A2_COLOR, A2_ALPHA } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

type View = 'schedule' | 'dilation' | 'rf';

type Layer = { k: number; d: number };
type Group = { label: string; from: number; to: number };
type SeqGroup = { label: string; k: number; dils: number[] };

const LAYERS: Layer[] = [
  { k: 6, d: 1 },
  { k: 6, d: 3 },
  { k: 6, d: 7 },
  { k: 6, d: 17 },
  { k: 6, d: 41 },
  { k: 6, d: 101 },
  { k: 6, d: 239 },
  { k: 6, d: 1 },
  { k: 6, d: 3 },
  { k: 6, d: 7 },
  { k: 6, d: 17 },
  { k: 6, d: 41 },
  { k: 6, d: 101 },
  { k: 6, d: 239 },
  { k: 15, d: 1 },
  { k: 15, d: 13 },
  { k: 6, d: 1 },
  { k: 6, d: 3 },
  { k: 6, d: 7 },
  { k: 6, d: 17 },
  { k: 6, d: 41 },
  { k: 6, d: 101 },
  { k: 6, d: 239 },
];

const GROUPS: Group[] = [
  { label: 'Dilation stack 1', from: 0, to: 6 },
  { label: 'Dilation stack 2', from: 7, to: 13 },
  { label: 'Degrid', from: 14, to: 15 },
  { label: 'Dilation stack 3', from: 16, to: 22 },
];

const SEQ_GROUPS: SeqGroup[] = [
  { label: 'Stack 1', k: 6, dils: [1, 3, 7, 17, 41, 101, 239] },
  { label: 'Stack 2', k: 6, dils: [1, 3, 7, 17, 41, 101, 239] },
  { label: 'Degrid', k: 15, dils: [1, 13] },
  { label: 'Stack 3', k: 6, dils: [1, 3, 7, 17, 41, 101, 239] },
];

/** Cumulative receptive field: rf[i] = 1 + sum_{j<=i} (k_j - 1) * d_j */
const RF = (() => {
  const out: number[] = [];
  let rf = 1;
  for (const { k, d } of LAYERS) {
    rf += (k - 1) * d;
    out.push(rf);
  }
  return out;
})();

const RF_TOTAL = RF[RF.length - 1];
const RF_MS = (RF_TOTAL / 48000) * 1000;

const YELLOW = A2_COLOR;
const YELLOW_DIM = A2_ALPHA(0.35);
const GRAY = 'rgba(160,160,168,1)';
const GRAY_DIM = 'rgba(160,160,168,0.35)';

const SUBTITLES: Record<View, string> = {
  schedule: 'Dilation shown large, kernel size below, degridding pair in yellow',
  dilation: 'Dilation per layer, with a doubling schedule for reference (dashed)',
  rf: 'Cumulative receptive field per layer, in samples of audio context at 48 kHz',
};

function SeqCell({ d, k, degrid }: { d: number; k: number; degrid: boolean }) {
  const footprint = (k - 1) * d + 1;
  return (
    <div
      title={`kernel ${k}, dilation ${d}, footprint ${footprint.toLocaleString()} samples`}
      className={`flex min-w-10 flex-col items-center justify-center rounded-lg px-1.5 py-2 ${
        degrid
          ? 'border border-[#FFFF00]/45 bg-[#FFFF00]/[0.06]'
          : 'border border-[#373739] bg-white/[0.03]'
      }`}
    >
      <div
        className={`text-base font-bold leading-tight tabular-nums ${
          degrid ? 'text-[#FFFF00]' : 'text-white'
        }`}
      >
        {d}
      </div>
      <div
        className={`mt-0.5 whitespace-nowrap text-[10px] ${
          degrid ? 'text-[#FFFF00]/70' : 'text-zinc-500'
        }`}
      >
        k = {k}
      </div>
    </div>
  );
}

function ScheduleGroup({ group }: { group: SeqGroup }) {
  const degrid = group.label === 'Degrid';
  return (
    <div>
      <div
        className={`mb-2 text-[11px] font-bold tracking-wide ${
          degrid ? 'text-[#FFFF00]' : 'text-white/45'
        }`}
      >
        {group.label}
      </div>
      <div className="flex gap-1.5">
        {group.dils.map((d, i) => (
          <SeqCell key={i} d={d} k={group.k} degrid={degrid} />
        ))}
      </div>
    </div>
  );
}

export default function A2DilationPattern() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const hoverRef = useRef(-1);
  const [view, setView] = useState<View>('schedule');

  useEffect(() => {
    if (view === 'schedule') return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w === 0 || h === 0) return;
      if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
        canvas.width = w * dpr;
        canvas.height = h * dpr;
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);

      const padL = 58;
      const padR = 20;
      const padT = 34;
      const padB = 44;
      const plotW = w - padL - padR;
      const plotH = h - padT - padB;
      const n = LAYERS.length;
      const toX = (i: number) => padL + ((i + 0.5) / n) * plotW;
      const hover = hoverRef.current;

      const tickFont = '13px Arial, sans-serif';
      const axisTitleFont = '14px Arial, sans-serif';
      const tickColor = 'rgba(255,255,255,0.85)';
      const axisTitleColor = 'rgba(255,255,255,0.9)';
      const gridColor = 'rgba(128,128,128,0.15)';

      ctx.font = 'bold 11px Arial, sans-serif';
      ctx.textBaseline = 'middle';
      GROUPS.forEach((g, gi) => {
        const x0 = padL + (g.from / n) * plotW;
        const x1 = padL + ((g.to + 1) / n) * plotW;
        if (gi > 0) {
          ctx.strokeStyle = 'rgba(255,255,255,0.12)';
          ctx.setLineDash([4, 4]);
          ctx.beginPath();
          ctx.moveTo(x0, padT - 18);
          ctx.lineTo(x0, padT + plotH);
          ctx.stroke();
          ctx.setLineDash([]);
        }
        const isDegrid = g.label === 'Degrid';
        ctx.fillStyle = isDegrid ? YELLOW : 'rgba(255,255,255,0.45)';
        ctx.textAlign = 'center';
        ctx.fillText(g.label.replace('Dilation stack', 'Stack'), (x0 + x1) / 2, padT - 24);
      });

      ctx.font = tickFont;
      ctx.fillStyle = tickColor;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      for (let i = 0; i < n; i += 1) {
        if (i % 2 === 0 || i === 14 || i === 15) {
          ctx.fillText(String(i), toX(i), padT + plotH + 8);
        }
      }
      ctx.font = axisTitleFont;
      ctx.fillStyle = axisTitleColor;
      ctx.fillText('Layer', padL + plotW / 2, padT + plotH + 26);

      if (view === 'dilation') {
        const yMax = 280;
        const toY = (d: number) => padT + (1 - d / yMax) * plotH;

        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        for (let dv = 0; dv <= 250; dv += 50) {
          const y = toY(dv);
          ctx.strokeStyle = dv === 0 ? 'rgba(255,255,255,0.35)' : gridColor;
          ctx.beginPath();
          ctx.moveTo(padL, y);
          ctx.lineTo(padL + plotW, y);
          ctx.stroke();
          ctx.font = tickFont;
          ctx.fillStyle = tickColor;
          ctx.fillText(String(dv), padL - 8, y);
        }

        ctx.save();
        ctx.translate(14, padT + plotH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.font = axisTitleFont;
        ctx.fillStyle = axisTitleColor;
        ctx.textAlign = 'center';
        ctx.fillText('Dilation', 0, 0);
        ctx.restore();

        const stackStarts = [0, 7, 16];
        ctx.strokeStyle = 'rgba(160,160,168,0.35)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        for (const s0 of stackStarts) {
          ctx.beginPath();
          for (let j = 0; j < 7; j++) {
            const x = toX(s0 + j);
            const y = toY(Math.pow(2, j));
            if (j === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.stroke();
        }
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(160,160,168,0.4)';
        for (const s0 of stackStarts) {
          for (let j = 0; j < 7; j++) {
            ctx.beginPath();
            ctx.arc(toX(s0 + j), toY(Math.pow(2, j)), 2.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }

        LAYERS.forEach((L, i) => {
          const x = toX(i);
          const y = toY(L.d);
          const wide = L.k === 15;
          const active = hover === i;
          const stemColor = wide ? YELLOW_DIM : GRAY_DIM;
          const dotColor = wide ? YELLOW : GRAY;
          ctx.strokeStyle = active ? dotColor : stemColor;
          ctx.lineWidth = wide ? 3 : 2;
          ctx.beginPath();
          ctx.moveTo(x, toY(0));
          ctx.lineTo(x, y);
          ctx.stroke();
          ctx.fillStyle = dotColor;
          ctx.beginPath();
          ctx.arc(x, y, (wide ? 6 : 4) + (active ? 1.5 : 0), 0, Math.PI * 2);
          ctx.fill();
          ctx.font = '13px Arial, sans-serif';
          ctx.fillStyle = wide ? YELLOW : 'rgba(255,255,255,0.85)';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
          ctx.fillText(String(L.d), x, y - (wide ? 9 : 7));
        });

        ctx.font = 'bold 13px Arial, sans-serif';
        ctx.textBaseline = 'middle';
        let lx = padL + 10;
        const ly = padT + 8;
        ctx.fillStyle = GRAY;
        ctx.beginPath();
        ctx.arc(lx + 4, ly, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.textAlign = 'left';
        ctx.fillText('k = 6', lx + 14, ly);
        lx += 66;
        ctx.fillStyle = YELLOW;
        ctx.beginPath();
        ctx.arc(lx + 4, ly, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillText('k = 15', lx + 16, ly);
        lx += 74;
        ctx.strokeStyle = 'rgba(160,160,168,0.5)';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(lx, ly);
        ctx.lineTo(lx + 20, ly);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(255,255,255,0.65)';
        ctx.fillText('doubling (1, 2, 4, … 64)', lx + 26, ly);
      } else {
        const yMax = 7000;
        const toY = (v: number) => padT + (1 - v / yMax) * plotH;

        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        for (let v = 0; v <= yMax; v += 1000) {
          const y = toY(v);
          ctx.strokeStyle = v === 0 ? 'rgba(255,255,255,0.35)' : gridColor;
          ctx.beginPath();
          ctx.moveTo(padL, y);
          ctx.lineTo(padL + plotW, y);
          ctx.stroke();
          ctx.font = tickFont;
          ctx.fillStyle = tickColor;
          ctx.fillText(v === 0 ? '0' : `${v / 1000}k`, padL - 8, y);
        }

        ctx.save();
        ctx.translate(14, padT + plotH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.font = axisTitleFont;
        ctx.fillStyle = axisTitleColor;
        ctx.textAlign = 'center';
        ctx.fillText('Receptive field (samples)', 0, 0);
        ctx.restore();

        ctx.strokeStyle = GRAY;
        ctx.lineWidth = 2.5;
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(toX(0), toY(1));
        RF.forEach((v, i) => ctx.lineTo(toX(i), toY(v)));
        ctx.stroke();

        RF.forEach((v, i) => {
          const wide = LAYERS[i].k === 15;
          const active = hover === i;
          ctx.fillStyle = wide ? YELLOW : GRAY;
          ctx.beginPath();
          ctx.arc(toX(i), toY(v), (wide ? 5 : 3) + (active ? 1.5 : 0), 0, Math.PI * 2);
          ctx.fill();
        });

        const ex = toX(n - 1);
        const ey = toY(RF_TOTAL);
        ctx.font = 'bold 13px Arial, sans-serif';
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(
          `${RF_TOTAL.toLocaleString()} samples ≈ ${Math.round(RF_MS)} ms`,
          ex - 4,
          ey - 10,
        );
      }

      if (hover >= 0 && hover < n) {
        const L = LAYERS[hover];
        const footprint = (L.k - 1) * L.d + 1;
        const lines =
          view === 'dilation'
            ? [
                `Layer ${hover}`,
                `Kernel ${L.k}, dilation ${L.d}`,
                `Footprint ${footprint.toLocaleString()} samples`,
              ]
            : [
                `Layer ${hover}`,
                `Kernel ${L.k}, dilation ${L.d}`,
                `RF ${RF[hover].toLocaleString()} samples (${((RF[hover] / 48000) * 1000).toFixed(1)} ms)`,
              ];
        ctx.font = '13px Arial, sans-serif';
        const tw = Math.max(...lines.map((s) => ctx.measureText(s).width)) + 20;
        const th = lines.length * 18 + 12;
        let tx = toX(hover) + 12;
        if (tx + tw > w - 4) tx = toX(hover) - tw - 12;
        const ty = padT + 6;
        ctx.fillStyle = 'rgba(24,24,27,0.95)';
        ctx.strokeStyle = '#373739';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(tx, ty, tw, th, 8);
        ctx.fill();
        ctx.stroke();
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        lines.forEach((s, li) => {
          ctx.font = li === 0 ? 'bold 13px Arial, sans-serif' : '13px Arial, sans-serif';
          ctx.fillStyle = li === 0 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.8)';
          ctx.fillText(s, tx + 10, ty + 7 + li * 18);
        });
      }
    };

    draw();

    const onMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const padL = 58;
      const plotW = rect.width - padL - 20;
      const i = Math.floor(((x - padL) / plotW) * LAYERS.length);
      const next = i >= 0 && i < LAYERS.length ? i : -1;
      if (next !== hoverRef.current) {
        hoverRef.current = next;
        draw();
      }
    };
    const onLeave = () => {
      hoverRef.current = -1;
      draw();
    };
    const onResize = () => draw();

    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);
    window.addEventListener('resize', onResize);
    return () => {
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
      window.removeEventListener('resize', onResize);
    };
  }, [view]);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="The A2 Dilation Pattern"
        description="All 23 layers of A2. Three dilation stacks share a hand-tuned, non-doubling schedule (1, 3, 7, 17, 41, 101, 239). Between stacks two and three, wide-kernel degridding layers fill in the gaps, possible because A2 mixes kernel sizes in one layer array."
      />
      <div className="w-full rounded-2xl border border-[#373739] p-4 sm:p-6">
        <div className="text-lg font-bold text-white">Kernel Size and Dilation per Layer</div>
        <div className="mb-3 mt-1.5 text-base text-zinc-400">{SUBTITLES[view]}</div>

        {view === 'schedule' ? (
          <div>
            <div className="flex flex-col gap-[18px] py-2">
              {/* Stack 1 alone */}
              <ScheduleGroup group={SEQ_GROUPS[0]} />
              {/* Stack 2 + Degrid on one row (degrid sits between stacks 2 and 3) */}
              <div className="flex flex-wrap items-start gap-x-[22px] gap-y-3">
                <ScheduleGroup group={SEQ_GROUPS[1]} />
                <ScheduleGroup group={SEQ_GROUPS[2]} />
              </div>
              {/* Stack 3 alone */}
              <ScheduleGroup group={SEQ_GROUPS[3]} />
            </div>
            <div className="mt-4 text-xs text-zinc-500">
              Receptive field: {RF_TOTAL.toLocaleString()} samples, about {Math.round(RF_MS)} ms at
              48 kHz
            </div>
          </div>
        ) : (
          <div className="relative h-[400px] w-full max-[500px]:h-[320px]">
            <canvas ref={canvasRef} className="block h-full w-full cursor-crosshair" />
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[#373739] pt-4 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-zinc-500">View</span>
            <div className="flex items-center gap-1 rounded-lg bg-white/5 p-0.5">
              {(
                [
                  { id: 'schedule', label: 'Schedule' },
                  { id: 'dilation', label: 'Dilation pattern' },
                  { id: 'rf', label: 'Receptive field' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setView(opt.id)}
                  className={`rounded-md px-2.5 py-1 transition ${
                    view === opt.id ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
